import React from 'react'
import { Input, Select, Switch } from 'antd'
import {
  FormWrapper,
  FieldsetContainer,
  FieldsetWrapper,
  LegendText,
  FieldRow,
  FieldLabel,
  LabelText,
  InputWrapper,
} from './InterestModellingForm.styles'

export interface InterestModellingFormData {
  // Loan & Property Details
  youngerAge: number
  projectionBasis: 'age' | 'term'
  customTerm: number
  initialLoan: number
  feesAdded: number
  interestBasis: 'MER' | 'AER'
  interestRate: number
  propertyValue: number
  propertyGrowth: number

  // Repayment Options
  includeRepayments: boolean
  repaymentType: 'monthly' | 'percent'
  monthlyAmount: number
  interestPercent: number
  repaymentTerm: number

  // Drawdown Options
  applyDrawdown: boolean
  drawdownAmount: number
  drawdownYear: number
}

interface InterestModellingFormProps {
  formData: InterestModellingFormData
  onChange: (field: keyof InterestModellingFormData, value: any) => void
}

const InterestModellingForm: React.FC<InterestModellingFormProps> = ({ formData, onChange }) => {
  return (
    <FormWrapper>
      <FieldsetWrapper>
        <LegendText>Loan & Property Details</LegendText>
        
        <FieldRow>
          <FieldLabel>
            <LabelText>Starting Age (Youngest Borrower):</LabelText>
            <InputWrapper>
              <Input
                type="number"
                min={50}
                max={84}
                value={formData.youngerAge}
                onChange={(e) => onChange('youngerAge', Number(e.target.value))}
                placeholder="e.g., 65"
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <LabelText>Projection Basis:</LabelText>
            <InputWrapper>
              <Select
                value={formData.projectionBasis}
                onChange={(value) => onChange('projectionBasis', value)}
                options={[
                  { label: 'Project to Age 85 (min 15 years)', value: 'age' },
                  { label: 'Custom Term (Years)', value: 'term' },
                ]}
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        {formData.projectionBasis === 'term' && (
          <FieldRow>
            <FieldLabel>
              <LabelText>Custom Term (Years):</LabelText>
              <InputWrapper>
                <Input
                  type="number"
                  min={1}
                  value={formData.customTerm}
                  onChange={(e) => onChange('customTerm', Number(e.target.value))}
                  placeholder="e.g., 20"
                />
              </InputWrapper>
            </FieldLabel>
          </FieldRow>
        )}

        <FieldRow>
          <FieldLabel>
            <LabelText>Initial loan amount (£):</LabelText>
            <InputWrapper>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={formData.initialLoan}
                onChange={(e) => onChange('initialLoan', Number(e.target.value))}
                placeholder="e.g., 100000"
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <LabelText>Plus Fees added to loan (£):</LabelText>
            <InputWrapper>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={formData.feesAdded}
                onChange={(e) => onChange('feesAdded', Number(e.target.value))}
                placeholder="e.g., 5000"
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <LabelText>Interest basis:</LabelText>
            <InputWrapper>
              <Select
                value={formData.interestBasis}
                onChange={(value) => onChange('interestBasis', value)}
                options={[
                  { label: 'MER (Monthly Effective Rate)', value: 'MER' },
                  { label: 'AER (Annual Effective Rate)', value: 'AER' },
                ]}
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <LabelText>{formData.interestBasis} (%):</LabelText>
            <InputWrapper>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={formData.interestRate}
                onChange={(e) => onChange('interestRate', Number(e.target.value))}
                placeholder={formData.interestBasis === 'MER' ? 'e.g., 0.5' : 'e.g., 5.0'}
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <LabelText>Property value (£):</LabelText>
            <InputWrapper>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={formData.propertyValue}
                onChange={(e) => onChange('propertyValue', Number(e.target.value))}
                placeholder="e.g., 250000"
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <LabelText>Property growth (% per year):</LabelText>
            <InputWrapper>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={formData.propertyGrowth}
                onChange={(e) => onChange('propertyGrowth', Number(e.target.value))}
                placeholder="e.g., 3.5"
              />
            </InputWrapper>
          </FieldLabel>
        </FieldRow>
      </FieldsetWrapper>

      <FieldsetContainer>
        <FieldsetWrapper>
          <LegendText>Repayment Options</LegendText>
          
          <FieldRow>
            <FieldLabel>
              <LabelText>Include repayments:</LabelText>
              <InputWrapper>
                <Switch
                  checked={formData.includeRepayments}
                  onChange={(checked) => onChange('includeRepayments', checked)}
                />
              </InputWrapper>
            </FieldLabel>
          </FieldRow>

          {formData.includeRepayments && (
            <>
              <FieldRow>
                <FieldLabel>
                  <LabelText>Repayment type:</LabelText>
                  <InputWrapper>
                    <Select
                      value={formData.repaymentType}
                      onChange={(value) => onChange('repaymentType', value)}
                      options={[
                        { label: 'Fixed Monthly Amount', value: 'monthly' },
                        { label: 'Percentage of Initial Annual Interest', value: 'percent' },
                      ]}
                    />
                  </InputWrapper>
                </FieldLabel>
              </FieldRow>

              {formData.repaymentType === 'monthly' ? (
                <FieldRow>
                  <FieldLabel>
                    <LabelText>Monthly amount (£):</LabelText>
                    <InputWrapper>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={formData.monthlyAmount}
                        onChange={(e) => onChange('monthlyAmount', Number(e.target.value))}
                        placeholder="e.g., 500"
                      />
                    </InputWrapper>
                  </FieldLabel>
                </FieldRow>
              ) : (
                <FieldRow>
                  <FieldLabel>
                    <LabelText>% of initial annual interest:</LabelText>
                    <InputWrapper>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={formData.interestPercent}
                        onChange={(e) => onChange('interestPercent', Number(e.target.value))}
                        placeholder="e.g., 20"
                      />
                    </InputWrapper>
                  </FieldLabel>
                </FieldRow>
              )}

              <FieldRow>
                <FieldLabel>
                  <LabelText>Repayment term (years):</LabelText>
                  <InputWrapper>
                    <Input
                      type="number"
                      min={0}
                      value={formData.repaymentTerm}
                      onChange={(e) => onChange('repaymentTerm', Number(e.target.value))}
                      placeholder="e.g., 10"
                    />
                  </InputWrapper>
                </FieldLabel>
              </FieldRow>
            </>
          )}
        </FieldsetWrapper>

        <FieldsetWrapper>
          <LegendText>Drawdown Options</LegendText>
          
          <FieldRow>
            <FieldLabel>
              <LabelText>Apply a drawdown (additional loan):</LabelText>
              <InputWrapper>
                <Switch
                  checked={formData.applyDrawdown}
                  onChange={(checked) => onChange('applyDrawdown', checked)}
                />
              </InputWrapper>
            </FieldLabel>
          </FieldRow>

          {formData.applyDrawdown && (
            <>
              <FieldRow>
                <FieldLabel>
                  <LabelText>Drawdown amount (£):</LabelText>
                  <InputWrapper>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      value={formData.drawdownAmount}
                      onChange={(e) => onChange('drawdownAmount', Number(e.target.value))}
                      placeholder="e.g., 20000"
                    />
                  </InputWrapper>
                </FieldLabel>
              </FieldRow>

              <FieldRow>
                <FieldLabel>
                  <LabelText>Year to apply drawdown:</LabelText>
                  <InputWrapper>
                    <Input
                      type="number"
                      min={1}
                      value={formData.drawdownYear}
                      onChange={(e) => onChange('drawdownYear', Number(e.target.value))}
                      placeholder="e.g., 5"
                    />
                  </InputWrapper>
                </FieldLabel>
              </FieldRow>
            </>
          )}
        </FieldsetWrapper>
      </FieldsetContainer>
    </FormWrapper>
  )
}

export default InterestModellingForm